/**
 * Persistence (§8) — IndexedDB via idb-keyval. No accounts, no server, no network at runtime.
 *
 * A Cloudflare Worker + KV leaderboard is a later option; the interface below is the stub the
 * brief asks for, and it is deliberately not implemented.
 */

import { get, set, del, keys } from 'idb-keyval';
import type { SaveFile } from '../engine/snapshot';
import type { Settings } from './session';

const K_SETTINGS = 'freeboard:settings';
const K_PROGRESS = 'freeboard:progress';
const K_SAVE_PREFIX = 'freeboard:save:';
const MAX_SAVES = 10;

export interface ScenarioResult {
  scenarioId: string;
  difficulty: string;
  passed: boolean;
  net: number;
  grade: string;
  atMs: number;
}

export interface Progress {
  best: Record<string, ScenarioResult>;
  unlocked: string[];
}

export async function loadSettings(): Promise<Partial<Settings> | null> {
  return (await get<Partial<Settings>>(K_SETTINGS)) ?? null;
}

export async function saveSettings(s: Settings): Promise<void> {
  await set(K_SETTINGS, s);
}

export async function loadProgress(): Promise<Progress> {
  return (await get<Progress>(K_PROGRESS)) ?? { best: {}, unlocked: ['s0-handover'] };
}

export async function recordResult(r: ScenarioResult, nextScenarioId: string | null): Promise<Progress> {
  const p = await loadProgress();
  const key = `${r.scenarioId}:${r.difficulty}`;
  const existing = p.best[key];
  if (!existing || r.net > existing.net) p.best[key] = r;
  if (r.passed && nextScenarioId && !p.unlocked.includes(nextScenarioId)) {
    p.unlocked.push(nextScenarioId);
  }
  await set(K_PROGRESS, p);
  return p;
}

export async function listSaves(): Promise<SaveFile[]> {
  const all = await keys();
  const out: SaveFile[] = [];
  for (const k of all) {
    if (typeof k === 'string' && k.startsWith(K_SAVE_PREFIX)) {
      const v = await get<SaveFile>(k);
      if (v) out.push(v);
    }
  }
  return out.sort((a, b) => b.savedAtMinute - a.savedAtMinute);
}

export async function putSave(save: SaveFile): Promise<void> {
  await set(`${K_SAVE_PREFIX}${save.name}`, save);
  const all = await listSaves();
  // Keep at most ten named saves (§8).
  for (const old of all.slice(MAX_SAVES)) await del(`${K_SAVE_PREFIX}${old.name}`);
}

export async function deleteSave(name: string): Promise<void> {
  await del(`${K_SAVE_PREFIX}${name}`);
}

/** Export a run as JSON (§8). */
export function exportRun(save: SaveFile): string {
  return JSON.stringify(save, null, 2);
}

export function importRun(json: string): SaveFile {
  const parsed = JSON.parse(json) as SaveFile;
  if (parsed.version !== 1) throw new Error(`unsupported save version ${String(parsed.version)}`);
  return parsed;
}

/**
 * Leaderboard stub (§8). Intentionally not implemented — the brief says leave the interface and
 * do not build it.
 */
export interface Leaderboard {
  submit(result: ScenarioResult): Promise<void>;
  top(scenarioId: string, difficulty: string, limit: number): Promise<ScenarioResult[]>;
}

export const leaderboard: Leaderboard | null = null;
